
import React from 'react';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle,
  Loader2,
  Flag
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface FlagQuestionButtonProps {
  questionId: number | string;
  reason?: string;
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  disabled?: boolean;
}

const FlagQuestionButton: React.FC<FlagQuestionButtonProps> = ({ 
  questionId,
  reason = 'Question lacks context or is incomplete',
  size = 'default',
  className,
  disabled = false
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleFlagQuestion = async () => {
    if (!questionId || disabled) return;

    setIsSubmitting(true);
    try {
      // Record the flag in database
      const { error } = await supabase
        .from('flagged_questions')
        .insert([
          { 
            question_id: questionId.toString(),
            reason: reason,
            status: 'pending',
            reported_at: new Date().toISOString()
          }
        ]);

      if (error) throw error;
      
      toast.success('Question reported successfully', {
        description: 'Thank you for helping improve our question quality'
      });
    } catch (error) {
      console.error('Error flagging question:', error);
      toast.error('Failed to report question', {
        description: 'Please try again later'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size={size}
            onClick={handleFlagQuestion}
            disabled={isSubmitting || disabled}
            className={`h-8 w-8 p-0 bg-secondary/20 text-secondary 
              hover:bg-secondary/30 hover:text-secondary
              active:scale-[0.90] active:bg-secondary/40 active:shadow-inner
              transition-all duration-300 ${className || 'absolute top-2 right-2 z-50'}`}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Flag className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent sideOffset={5}>
          <p>Report this question</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FlagQuestionButton;
